import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. Le verre a son propre projecteur.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'pane',
  family: 'geometric',
  danceability: 0.90,
  requiresTier: 0,
  energyLevel: 0.90,
  bpmMin: 120,
  bpmMax: 175,
  movement: 0.90,
  tonality: 0.35,
  prefers: { lowEnd: [0.55, 1.0] },
  enabled: true
};

// PANE : une grande vitre noire fissurée plein écran, les fissures en néon
// sur des cellules de Voronoï ; la lumière COURT dans les fissures sur les
// temps, de nouvelles fissures se tracent sur les mesures, et au drop la
// vitre ÉCLATE en 3D puis se recompose en une vitre nouvelle. LOOK VDJ party
// (lib/vdjLook.js). Techno, bass music, big room, hardstyle. (Le brief
// l'appelait Shatter ; cet id existait déjà pour la pochette-vitre, les deux
// sont gardés.)
//
// LA VITRE (CPU, tools : rien, tout ici). Germes de Voronoï par disque de
// Poisson à rayon variable : denses au centre de l'écran (le point d'impact,
// R19 : le centre de composition), larges vers les bords. Delaunay par
// Bowyer-Watson, puis chaque cellule = le rectangle de la vitre coupé par les
// médiatrices de ses voisins de Delaunay ; chaque côté du polygone porte le
// voisin qui l'a produit (ou le bord de la vitre). Chaque cellule est
// RÉÉCHANTILLONNÉE à 12 sommets en coupant ses plus longs côtés en deux : le
// polygone reste exact, et deux vitres se morphent sommet à sommet. Le graphe
// des arêtes reçoit une distance GÉODÉSIQUE depuis le sommet le plus proche
// de l'impact (Dijkstra) : c'est le long de ce graphe que la lumière court.
//
// UN SEUL MESH d'éclats : un prisme fin à 12 côtés par cellule (face, dos,
// flancs), les anneaux A (vitre courante) et B (vitre suivante) en attributs,
// la transformation rigide de chaque cellule (position, échelle, quaternion)
// dans une texture flottante 2 x 256 réécrite par frame (8 Ko). Ce mesh EST
// la vitre quand elle est intacte (transformations identité) et les éclats
// quand elle vole : aucun objet ajouté ni retiré.
//
// LES FISSURES (R1) : un quad instancié par CÔTÉ de cellule (12 par cellule),
// qui suit la transformation de SA cellule : au vol, chaque arête part avec
// son éclat ; intacte, les deux copies d'une arête partagée coïncident. Le
// quad est étendu dans l'ESPACE ÉCRAN (le segment projeté, élargi de la
// marge en pixels) et le fragment mesure sa distance au segment EN PIXELS :
// cœur blanc 1 à 1,5 px, gaine de couleur 2,5 à 4 px, halo exp(-d/10) x 0,35
// qui s'annule avant le bord du quad (R12). Fusion MAX (EXT_blend_minmax,
// natif en WebGL2) : deux arêtes qui se recouvrent prennent le maximum, pas
// la somme - un additif faisait une perle à chaque sommet de Voronoï, où
// trois gaines s'empilaient au-dessus du seuil de bloom.
//
// LA VIE : sur chaque temps une impulsion part de l'impact et parcourt le
// graphe en un demi-temps (géodésique) ; bands.high fait scintiller les
// petites fissures, bands.low fait respirer l'épaisseur des fissures
// radiales ; sur chaque mesure 8 à 15 germes sont ajoutés près d'un point
// et les arêtes nouvelles se TRACENT : un front part du point d'insertion et
// avance le long du réseau (seconde géodésique, 0,6 unité par temps), chaque
// arête apparaissant quand il l'atteint (une arête est identifiée par sa
// paire de germes, les germes ne bougeant jamais : une arête ancienne
// raccourcie garde sa date, une paire nouvelle naît maintenant).
//
// L'ÉCLATEMENT : au drop (annoncé par director.nextEvent, ou la marque
// franchie) ou à la fin d'une scène de 20 s sans drop (R20,
// lib/scene-clock.js) : vitesse radiale depuis l'impact, rotation aléatoire,
// gravité nulle, traînée légère ; un projecteur tourne (spéculaire fort) ; la
// caméra recule de 60 % en 2 mesures. Puis RECOMPOSITION en 1 mesure vers
// une vitre NOUVELLE : les éclats sont appariés aux nouvelles cellules par
// proximité (glouton sur les distances triées), morphés anneau A -> anneau B
// pendant leur retour ; les cellules en trop naissent d'un point et les
// éclats en trop rétrécissent en s'éloignant ; flash blanc (VdjComfort) au
// moment où la vitre se referme.
//
// RYTHME : temps = impulsion + séparation RGB (VdjPost) ; mesure = fissures
// nouvelles + à-coup de caméra ; phrase = palette suivante ; montée (drop
// annoncé sous 8 mesures) = la vitre vibre (micro-déplacement par cellule,
// croissant), les fissures se multiplient, la lumière accélère.
//
// Tier 0 : 50 germes, projecteur fixe. Repli sans BPM : horloge libre à
// 128 ; sans drop, l'éclatement de fin de scène ; sans pochette, palette de
// secours ; en pause, tout gèle.
// Debug : ?pane=1 (éclatement immédiat), ?paneseeds=<n>, ?panegraph=1 (la
// géodésique en teinte), ?paneprobe=1 (window.__pane).
//
// precision highp (exception documentée) : les positions écran en pixels et
// les distances au segment ne tiennent pas en fp16 sur la TV.

const MAX_CELLS = 256;
const RING = 12;
const FOV = 40;                       // deg
const BLEED = 1.10;                   // the pane beyond the frame (roll, shake)
const DEPTH = 0.03;                   // prism thickness, pane units (height 2)
const TIER = {
  0: { seeds: 50, spin: false, specPow: 64, specK: 0.10 },
  1: { seeds: 90, spin: true, specPow: 64, specK: 0.10 },
  2: { seeds: 130, spin: true, specPow: 64, specK: 0.10 }
};
const CYCLE = 20;                     // s, the scene (R20)
const FLIGHT_BARS = 2, RECOMPOSE_BARS = 1;
const NO_BPM = 128;
const SAFE_FLOOR = 0.5;
const LUM_W = 64, LUM_H = 36;
const MARGIN_PX = 26;                 // half-width of a crack quad: halo gone by then
const ACCENT_FALLBACK = [0x2bffea, 0xff2bd6, 0xffc21a];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function mulberry(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- the glass: Poisson sites, Delaunay, Voronoi cells, geodesics ---------

// variable-radius dart throwing: r grows with the distance to the impact
function poissonSites(rng, hx, hy, target) {
  const rmax = Math.hypot(hx, hy);
  let r0 = Math.sqrt((4 * hx * hy) / target) * 0.80;
  let pts = [];
  for (let iter = 0; iter < 10; iter++) {
    pts = [];
    let fails = 0;
    while (pts.length < target && fails < 3000) {
      const x = (rng() * 2 - 1) * hx * 0.985, y = (rng() * 2 - 1) * hy * 0.985;
      const rr = r0 * (0.5 + 1.1 * Math.hypot(x, y) / rmax);
      let ok = true;
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        if (Math.hypot(p.x - x, p.y - y) < Math.min(rr, p.r)) { ok = false; break; }
      }
      if (ok) { pts.push({ x, y, r: rr }); fails = 0; } else fails++;
    }
    if (pts.length >= target) break;
    r0 *= 0.88;
  }
  return pts.map((p) => ({ x: p.x, y: p.y }));
}

// Bowyer-Watson. Returns triangles as index triples (sites only).
function delaunay(P) {
  const n = P.length;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of P) { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }
  const d = Math.max(maxX - minX, maxY - minY) * 50, cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  const V = P.map((p) => [p.x, p.y]);
  V.push([cx - d, cy - d * 0.6], [cx, cy + d], [cx + d, cy - d * 0.6]);
  const tri = (a, b, c) => {
    const ax = V[a][0], ay = V[a][1], bx = V[b][0], by = V[b][1], qx = V[c][0], qy = V[c][1];
    const D = 2 * (ax * (by - qy) + bx * (qy - ay) + qx * (ay - by));
    if (Math.abs(D) < 1e-14) return null;
    const ux = ((ax * ax + ay * ay) * (by - qy) + (bx * bx + by * by) * (qy - ay) + (qx * qx + qy * qy) * (ay - by)) / D;
    const uy = ((ax * ax + ay * ay) * (qx - bx) + (bx * bx + by * by) * (ax - qx) + (qx * qx + qy * qy) * (bx - ax)) / D;
    return { a, b, c, ux, uy, r2: (ax - ux) * (ax - ux) + (ay - uy) * (ay - uy), bad: false };
  };
  let tris = [tri(n, n + 1, n + 2)];
  for (let i = 0; i < n; i++) {
    const px = V[i][0], py = V[i][1];
    const edges = new Map();
    for (const t of tris) {
      if ((px - t.ux) * (px - t.ux) + (py - t.uy) * (py - t.uy) <= t.r2 * (1 + 1e-12)) {
        t.bad = true;
        for (const [u, v] of [[t.a, t.b], [t.b, t.c], [t.c, t.a]]) {
          const k = u < v ? u * 4096 + v : v * 4096 + u;
          edges.set(k, (edges.get(k) || 0) + 1);
        }
      }
    }
    const keep = [];
    for (const t of tris) if (!t.bad) keep.push(t);
    for (const t of tris) {
      if (!t.bad) continue;
      for (const [u, v] of [[t.a, t.b], [t.b, t.c], [t.c, t.a]]) {
        const k = u < v ? u * 4096 + v : v * 4096 + u;
        if (edges.get(k) === 1) { const nt = tri(u, v, i); if (nt) keep.push(nt); edges.set(k, 0); }
      }
    }
    tris = keep;
  }
  return tris.filter((t) => t.a < n && t.b < n && t.c < n);
}

// Sutherland-Hodgman against the half-plane (p - m) . d <= 0. Each vertex
// carries the owner of the side that STARTS at it.
function clipHalf(poly, mx, my, dx, dy, tag) {
  const out = [], n = poly.length;
  for (let i = 0; i < n; i++) {
    const P = poly[i], Q = poly[(i + 1) % n];
    const sp = (P.x - mx) * dx + (P.y - my) * dy, sq = (Q.x - mx) * dx + (Q.y - my) * dy;
    const inP = sp <= 0, inQ = sq <= 0;
    if (inP) out.push(P);
    if (inP !== inQ) {
      const t = sp / (sp - sq);
      out.push({ x: P.x + (Q.x - P.x) * t, y: P.y + (Q.y - P.y) * t, tag: inP ? tag : P.tag });
    }
  }
  return out;
}

// the whole glass: cells (12 ring points each, with geodesic g) and the
// graph's longest geodesic
function buildGlass(sites, hx, hy, from) {
  const n = sites.length;
  const tris = delaunay(sites);
  const nb = Array.from({ length: n }, () => new Set());
  for (const t of tris) { nb[t.a].add(t.b); nb[t.a].add(t.c); nb[t.b].add(t.a); nb[t.b].add(t.c); nb[t.c].add(t.a); nb[t.c].add(t.b); }
  const polys = [];
  for (let i = 0; i < n; i++) {
    let poly = [{ x: -hx, y: -hy, tag: -1 }, { x: hx, y: -hy, tag: -2 }, { x: hx, y: hy, tag: -3 }, { x: -hx, y: hy, tag: -4 }];
    const s = sites[i];
    for (const j of nb[i]) {
      const o = sites[j];
      poly = clipHalf(poly, (s.x + o.x) / 2, (s.y + o.y) / 2, o.x - s.x, o.y - s.y, j);
      if (poly.length < 3) break;
    }
    // drop near-duplicate consecutive vertices (the side's owner survives on the first)
    const clean = [];
    for (let k = 0; k < poly.length; k++) {
      const p = poly[k], q = poly[(k + 1) % poly.length];
      if (Math.hypot(p.x - q.x, p.y - q.y) > 1e-7) clean.push(p);
      else q.tag = p.tag;
    }
    polys.push(clean);
  }
  // the graph: nodes = distinct vertices, edges = interior sides
  const nodeId = new Map(), nodes = [];
  const key = (p) => Math.round(p.x * 1e5) + ',' + Math.round(p.y * 1e5);
  const node = (p) => { const k = key(p); let id = nodeId.get(k); if (id == null) { id = nodes.length; nodeId.set(k, id); nodes.push({ x: p.x, y: p.y, adj: [] }); } return id; };
  for (const poly of polys) {
    for (let k = 0; k < poly.length; k++) {
      const p = poly[k], q = poly[(k + 1) % poly.length];
      const a = node(p), b = node(q);
      if (p.tag >= 0 && a !== b) { const l = Math.hypot(p.x - q.x, p.y - q.y); nodes[a].adj.push([b, l]); nodes[b].adj.push([a, l]); }
    }
  }
  const V = nodes.length;
  // Dijkstra from the node nearest a point (O(V^2), V ~ 2 x the cells)
  const geodesic = (px, py) => {
    const dist = new Float64Array(V).fill(Infinity), done = new Uint8Array(V);
    let src = 0, bd = Infinity;
    for (let v = 0; v < V; v++) { const d = Math.hypot(nodes[v].x - px, nodes[v].y - py); if (d < bd && nodes[v].adj.length) { bd = d; src = v; } }
    dist[src] = 0;
    for (let it = 0; it < V; it++) {
      let u = -1, best = Infinity;
      for (let v = 0; v < V; v++) if (!done[v] && dist[v] < best) { best = dist[v]; u = v; }
      if (u < 0) break;
      done[u] = 1;
      for (const [v, l] of nodes[u].adj) if (dist[u] + l < dist[v]) dist[v] = dist[u] + l;
    }
    return dist;
  };
  const dist = geodesic(0, 0);                                  // the light's pulses: from the impact
  const dist2 = from ? geodesic(from.x, from.y) : null;         // a new crack's drawing: from its insertion point
  let gmax = 0;
  for (let v = 0; v < V; v++) if (dist[v] < Infinity) gmax = Math.max(gmax, dist[v]);
  if (gmax <= 0) gmax = 1;
  const cells = [];
  for (let i = 0; i < n; i++) {
    const poly = polys[i];
    let area = 0, cx = 0, cy = 0;
    for (let k = 0; k < poly.length; k++) {
      const p = poly[k], q = poly[(k + 1) % poly.length];
      const w = p.x * q.y - q.x * p.y;
      area += w; cx += (p.x + q.x) * w; cy += (p.y + q.y) * w;
    }
    area *= 0.5;
    if (Math.abs(area) > 1e-12) { cx /= 6 * area; cy /= 6 * area; } else { cx = sites[i].x; cy = sites[i].y; }
    const v = poly.map((p) => { const id = nodeId.get(key(p)), g = dist[id], g2 = dist2 ? dist2[id] : 0; return { x: p.x, y: p.y, tag: p.tag, g: g < Infinity ? g : gmax, g2: g2 < Infinity ? g2 : 9 }; });
    cells.push({ id: i, ring: ring12(v, cx, cy), cx, cy, area: Math.abs(area) });
  }
  return { cells, gmax, sites: sites.slice() };
}

// exactly RING vertices: merge the two shortest adjacent sides while too
// many, split the longest side while too few; start at the smallest polar
// angle from the centroid so two cells morph vertex to vertex
function ring12(poly, cx, cy) {
  const v = poly.map((p) => ({ x: p.x, y: p.y, tag: p.tag, g: p.g, g2: p.g2 }));
  while (v.length > RING) {
    let best = -1, bl = Infinity;
    for (let i = 0; i < v.length; i++) {
      const a = v[(i - 1 + v.length) % v.length], b = v[i], c = v[(i + 1) % v.length];
      const l = Math.hypot(b.x - a.x, b.y - a.y) + Math.hypot(c.x - b.x, c.y - b.y);
      if (l < bl) { bl = l; best = i; }
    }
    v.splice(best, 1);
  }
  while (v.length < RING) {
    let best = 0, bl = -1;
    for (let i = 0; i < v.length; i++) {
      const a = v[i], b = v[(i + 1) % v.length];
      const l = Math.hypot(b.x - a.x, b.y - a.y);
      if (l > bl) { bl = l; best = i; }
    }
    const a = v[best], b = v[(best + 1) % v.length];
    v.splice(best + 1, 0, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, tag: a.tag, g: Math.min(a.g, b.g) + bl / 2, g2: Math.min(a.g2, b.g2) + bl / 2 });
  }
  let s = 0, ba = Infinity;
  for (let i = 0; i < v.length; i++) {
    let an = Math.atan2(v[i].y - cy, v[i].x - cx);
    if (an < 0) an += Math.PI * 2;
    if (an < ba) { ba = an; s = i; }
  }
  return v.slice(s).concat(v.slice(0, s));
}

// --- shaders ------------------------------------------------------------

const SAFE_GLSL = `
  uniform vec2 uScreen;
  uniform float uAspect;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt() {
    vec2 s = (gl_FragCoord.xy / uScreen * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;
const CELL_GLSL = `
  uniform sampler2D uCells;
  uniform float uCellsN, uMorph, uShake, uTime;
  vec3 qrot(vec4 q, vec3 v) { return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v); }
  float hash1(float n) { return fract(sin(n * 127.1 + 311.7) * 43758.5453); }
  // the build-up vibration: one rigid jitter per cell, pixels -> pane units
  vec2 shake(float cell) {
    float h = hash1(cell);
    return uShake * (2.0 / uScreen.y) * (0.5 + 0.5 * h) * vec2(sin(uTime * 37.0 + h * 6.28), cos(uTime * 43.0 + h * 9.1));
  }
`;

const shardVertex = `
  precision highp float;
  attribute float aCell;
  attribute vec3 aPA, aPB, aNA, aNB;
  attribute float aSide;
  uniform vec2 uScreen;
  ${CELL_GLSL}
  varying vec3 vW, vN;
  varying float vSide, vCell;
  void main() {
    float row = (aCell + 0.5) / uCellsN;
    vec4 t0 = texture2D(uCells, vec2(0.25, row));
    vec4 q = texture2D(uCells, vec2(0.75, row));
    vec3 l = mix(aPA, aPB, uMorph) * t0.w;
    l.xy += shake(aCell);
    vec3 n = normalize(mix(aNA, aNB, uMorph));
    // each facet tilted a little, so the pane reads as facets, not a plane
    if (aSide < 0.5) { float h = hash1(aCell + 7.0), h2 = hash1(aCell + 13.0); n = normalize(n + vec3((h - 0.5) * 0.08, (h2 - 0.5) * 0.08, 0.0)); }
    vW = t0.xyz + qrot(q, l);
    vN = qrot(q, n);
    vSide = aSide; vCell = aCell;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(vW, 1.0);
  }
`;
const shardFragment = `
  precision highp float;
  ${SAFE_GLSL}
  uniform vec3 uLight, uTint;
  uniform float uSpecPow, uSpecK, uFlash, uGain, uGraph;
  varying vec3 vW, vN;
  varying float vSide, vCell;
  void main() {
    vec3 V = normalize(cameraPosition - vW);
    vec3 N = normalize(vN);
    if (!gl_FrontFacing) N = -N;
    vec3 L = normalize(uLight);              // a far projector: one highlight per facet, a soft gradient across the pane
    vec3 H = normalize(L + V);
    float spec = pow(max(dot(N, H), 0.0), uSpecPow) * uSpecK;
    vec3 R = reflect(-V, N);
    float env = smoothstep(-0.3, 0.9, R.y) * 0.025 + smoothstep(0.5, 1.0, R.x) * 0.012;
    float fres = pow(1.0 - max(dot(N, V), 0.0), 5.0);
    vec3 base = uTint * (0.012 + vSide * 0.10);
    vec3 c = base + uTint * env * (0.6 + 0.4 * fres) + mix(vec3(1.0), uTint, 0.5) * spec;
    if (uGraph > 0.5) c = vec3(0.05);
    c = mix(c, vec3(1.0), uFlash);
    gl_FragColor = vec4(c * safeAtt() * uGain, 1.0);
  }
`;

const crackVertex = `
  precision highp float;
  attribute float aCell, aVis;
  attribute vec2 aA0, aA1, aB0, aB1, aG, aG2;
  attribute vec4 aInfo;        // radial weight, birth, (unused), small
  uniform vec2 uScreen;
  uniform float uZ, uMargin;
  ${CELL_GLSL}
  varying vec2 vS0, vS1, vG, vG2;
  varying float vL, vCell;
  varying vec4 vInfo;
  void main() {
    if (aVis < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float row = (aCell + 0.5) / uCellsN;
    vec4 t0 = texture2D(uCells, vec2(0.25, row));
    vec4 q = texture2D(uCells, vec2(0.75, row));
    vec2 sh = shake(aCell);
    vec2 l0 = mix(aA0, aB0, uMorph) * t0.w + sh, l1 = mix(aA1, aB1, uMorph) * t0.w + sh;
    vec3 w0 = t0.xyz + qrot(q, vec3(l0, uZ)), w1 = t0.xyz + qrot(q, vec3(l1, uZ));
    vec4 c0 = projectionMatrix * modelViewMatrix * vec4(w0, 1.0);
    vec4 c1 = projectionMatrix * modelViewMatrix * vec4(w1, 1.0);
    if (c0.w <= 0.01 || c1.w <= 0.01) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec2 s0 = (c0.xy / c0.w * 0.5 + 0.5) * uScreen, s1 = (c1.xy / c1.w * 0.5 + 0.5) * uScreen;
    vec2 d = s1 - s0;
    float L = length(d);
    d = L > 1e-4 ? d / L : vec2(1.0, 0.0);
    vec2 n = vec2(-d.y, d.x);
    float u = position.x, v = position.y;
    vec2 base = mix(s0, s1, u);
    vec2 s = base + d * (u * 2.0 - 1.0) * uMargin + n * v * uMargin;
    vec4 c = mix(c0, c1, u);
    c.xy += (s - base) / uScreen * 2.0 * c.w;
    gl_Position = c;
    vS0 = s0; vS1 = s1; vG = aG; vG2 = aG2; vInfo = aInfo; vCell = aCell;
    vL = length(l1 - l0);
  }
`;
const crackFragment = `
  precision highp float;
  ${SAFE_GLSL}
  uniform vec3 uColA, uColB;
  uniform float uTime, uBase, uPulseS, uPulseA, uPulseW, uHigh, uBreath, uDraw, uMargin, uGain, uGraph, uGmax, uCore, uSheath, uApart;
  varying vec2 vS0, vS1, vG, vG2;
  varying float vL, vCell;
  varying vec4 vInfo;
  float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  void main() {
    vec2 f = gl_FragCoord.xy;
    vec2 e = vS1 - vS0;
    float L2 = dot(e, e);
    float t = L2 > 0.0 ? clamp(dot(f - vS0, e) / L2, 0.0, 1.0) : 0.0;
    float d = length(f - (vS0 + e * t));
    // a crack still being drawn GROWS from its insertion point along the
    // network: a point is drawn once the front (uDraw pane units per second
    // since the birth) has passed its geodesic from that point
    float s = t * vL;
    float gn = min(vG2.x + s, vG2.y + (vL - s));
    float front = (uTime - vInfo.y) * uDraw;
    if (gn > front) discard;
    float prog = clamp((front - gn) / 0.25, 0.0, 1.0);          // how long ago the front passed (pane units)
    float tip = 1.0 + 0.6 * (1.0 - smoothstep(0.0, 0.06, front - gn)) * step(front, 8.0);
    // the geodesic at this point, from both ends of the side
    float g = min(vG.x + s, vG.y + (vL - s));
    float x = (g - uPulseS) / uPulseW;
    float pulse = exp(-x * x) + 0.35 * exp(-max(uPulseS - g, 0.0) / (uPulseW * 2.5)) * step(g, uPulseS);
    float I = uBase + uPulseA * pulse;
    if (vInfo.w > 0.5) { float ft = uTime * 12.0; float fl = mix(hash2(vec2(vCell, floor(ft))), hash2(vec2(vCell, floor(ft) + 1.0)), fract(ft)); I *= 1.0 - 0.35 * uHigh * fl; }
    I = min(I * tip, 1.6);
    float wc = mix(uCore, uCore * 1.5, vInfo.x) * (1.0 + 0.6 * uBreath * vInfo.x);
    float ws = mix(uSheath, uSheath * 1.6, vInfo.x) * (1.0 + 0.6 * uBreath * vInfo.x);
    float sub = min(wc, 1.0);
    wc = max(wc, 1.0);
    float core = (1.0 - smoothstep(wc - 0.5, wc + 0.5, d)) * sub;
    float sheath = 1.0 - smoothstep(ws - 1.0, ws + 1.0, d);
    // the halo arrives once the WHOLE side is drawn: on a half-drawn side it
    // read as a glow dot around a stub
    float halo = exp(-d / 10.0) * 0.30 * smoothstep(uMargin, uMargin - 8.0, d) * smoothstep(0.0, 0.2, front - max(vG2.x, vG2.y));
    vec3 col = mix(uColA, uColB, vInfo.x);
    if (uGraph > 0.5) { float hh = fract(g / uGmax * 0.8); col = clamp(abs(fract(hh + vec3(0.0, 0.667, 0.333)) * 6.0 - 3.0) - 1.0, 0.0, 1.0); }
    vec3 c = vec3(1.0) * core * smoothstep(0.5, 0.9, I) + col * (0.7 * sheath + halo) * min(I, 1.0);
    gl_FragColor = vec4(c * safeAtt() * uGain * uApart, 1.0);
  }
`;

const lumFragment = `
  precision highp float;
  varying vec2 vUv;
  uniform sampler2D uTex;
  void main() { gl_FragColor = texture2D(uTex, vUv); }
`;

const _q = new THREE.Quaternion(), _qi = new THREE.Quaternion(), _v = new THREE.Vector3();

export class PaneVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._layout = TIER[this._tier];
    const n = parseInt(urlParam('paneseeds'), 10);
    this._target = Number.isFinite(n) ? Math.max(8, Math.min(MAX_CELLS - 20, n)) : this._layout.seeds;
    this._burstNow = urlParam('pane') === '1';
    this._graph = urlParam('panegraph') === '1';
    this._post = new VdjPost({ bloomStrength: 0.25, bloomThreshold: 0.78, grain: 0.015, vignette: 0.3 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    this._clock = new SceneClock({ period: CYCLE, start: 0 });
    this._rng = mulberry(0x9E3779B9 ^ Math.floor((this._clock.elapsed + 1) * 1000));
    if (urlParam('paneprobe') === '1' && typeof window !== 'undefined') window.__pane = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    this._time = 0; this._beats = 0; this._beatCount = 0; this._barCount = 0; this._phraseIdx = 0;
    this._beatPeriod = 60 / NO_BPM; this._free = 0; this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrasePhase = null; this._lastBeatIndex = null;
    this._lastBeatAt = -10; this._build = 0; this._low = 0.3; this._high = 0.3; this._energy = 0.6;
    this._state = 'intact'; this._stateT = 0; this._burstCount = 0; this._burstInScene = false; this._lastDropKey = null; this._dropHoldUntil = -1;
    this._aspect = 0; this._hx = 1.78 * BLEED; this._hy = BLEED; this._recede = 0; this._morph = 0;
    this._palette = []; this._palIdx = 0;
    this._cols = [new THREE.Color(0.2, 1.0, 0.9), new THREE.Color(1.0, 0.2, 0.8)];
    this._lumAge = 0; this._lumPending = false; this._lumFresh = false; this._lumMeasured = 0.1; this._lumSince = 0; this._measuring = false; this._lumPixels = new Uint8Array(LUM_W * LUM_H * 4);
    this._flash = 0; this._blendChecked = false;
    this._slots = [];           // the flying/standing cells
    this._next = null;          // the glass being recomposed to
    this._births = new Map();

    // the per-cell transform texture: (x y z scale) (qx qy qz qw)
    this._cellData = new Float32Array(MAX_CELLS * 8);
    this._cellTex = new THREE.DataTexture(this._cellData, 2, MAX_CELLS, THREE.RGBAFormat, THREE.FloatType);
    this._cellTex.minFilter = this._cellTex.magFilter = THREE.NearestFilter; this._cellTex.needsUpdate = true;

    const safe = { uScreen: { value: new THREE.Vector2(1280, 720) }, uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    const cell = { uCells: { value: this._cellTex }, uCellsN: { value: MAX_CELLS }, uMorph: { value: 0 }, uShake: { value: 0 }, uTime: { value: 0 } };
    this._gain = { value: 1 }; this._flashU = { value: 0 }; this._graphU = { value: this._graph ? 1 : 0 };
    this._shardU = Object.assign({}, safe, cell, {
      uLight: { value: new THREE.Vector3(1.5, 1.0, 2.2) }, uTint: { value: new THREE.Color(0.3, 0.9, 1.0) },
      uSpecPow: { value: this._layout.specPow }, uSpecK: { value: this._layout.specK }, uFlash: this._flashU, uGain: this._gain, uGraph: this._graphU
    });
    this._crackU = Object.assign({}, safe, cell, {
      uZ: { value: DEPTH / 2 + 0.004 }, uMargin: { value: MARGIN_PX }, uColA: { value: this._cols[0] }, uColB: { value: this._cols[1] },
      uBase: { value: 0.35 }, uPulseS: { value: -10 }, uPulseA: { value: 0.9 }, uPulseW: { value: 0.12 }, uHigh: { value: 0 }, uBreath: { value: 0 },
      uDraw: { value: 0.45 }, uGain: this._gain, uGraph: this._graphU, uGmax: { value: 1 }, uCore: { value: 0.9 }, uSheath: { value: 1.5 }, uApart: { value: 1 }
    });

    // the shard mesh: MAX_CELLS prisms of RING sides
    const VPC = RING * 2 + RING * 4, IPC = (RING - 2) * 3 * 2 + RING * 6;
    const nV = MAX_CELLS * VPC;
    this._shardGeo = new THREE.BufferGeometry();
    this._attr = {
      aCell: new Float32Array(nV), aPA: new Float32Array(nV * 3), aPB: new Float32Array(nV * 3), aNA: new Float32Array(nV * 3), aNB: new Float32Array(nV * 3), aSide: new Float32Array(nV)
    };
    const idx = new Uint32Array(MAX_CELLS * IPC);
    for (let c = 0; c < MAX_CELLS; c++) {
      const b = c * VPC; let o = c * IPC;
      for (let k = 1; k < RING - 1; k++) { idx[o++] = b; idx[o++] = b + k; idx[o++] = b + k + 1; }                               // front (CCW seen from +z)
      for (let k = 1; k < RING - 1; k++) { idx[o++] = b + RING; idx[o++] = b + RING + k + 1; idx[o++] = b + RING + k; }        // back
      for (let k = 0; k < RING; k++) { const s = b + 2 * RING + k * 4; idx[o++] = s; idx[o++] = s + 1; idx[o++] = s + 2; idx[o++] = s; idx[o++] = s + 2; idx[o++] = s + 3; }
      for (let v = 0; v < VPC; v++) { this._attr.aCell[b + v] = c; this._attr.aSide[b + v] = v >= 2 * RING ? 1 : 0; }
    }
    this._shardGeo.setIndex(new THREE.BufferAttribute(idx, 1));
    this._shardGeo.setAttribute('position', new THREE.BufferAttribute(this._attr.aPA, 3));   // three.js needs one; aPA is also the position
    for (const [name, arr] of Object.entries(this._attr)) this._shardGeo.setAttribute(name, new THREE.BufferAttribute(arr, name === 'aCell' || name === 'aSide' ? 1 : 3));
    this._shardGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._VPC = VPC; this._IPC = IPC;
    this._shardMat = new THREE.ShaderMaterial({ uniforms: this._shardU, vertexShader: shardVertex, fragmentShader: shardFragment, side: THREE.DoubleSide });
    this._shards = new THREE.Mesh(this._shardGeo, this._shardMat);
    this._shards.frustumCulled = false; this._shards.renderOrder = 0;
    const self = this;
    this._shards.onBeforeRender = function (renderer, sc, cam) { self._beforeRender(renderer, sc, cam); };

    // the cracks: MAX_CELLS x RING instanced quads
    const nI = MAX_CELLS * RING;
    this._crackGeo = new THREE.InstancedBufferGeometry();
    const quad = new THREE.BufferGeometry();
    quad.setAttribute('position', new THREE.BufferAttribute(new Float32Array([0, -1, 0, 1, -1, 0, 1, 1, 0, 0, 1, 0]), 3));
    quad.setIndex([0, 1, 2, 0, 2, 3]);
    this._crackGeo.index = quad.index;
    this._crackGeo.setAttribute('position', quad.getAttribute('position'));
    this._iattr = { aCell: [1, new Float32Array(nI)], aVis: [1, new Float32Array(nI)], aA0: [2, new Float32Array(nI * 2)], aA1: [2, new Float32Array(nI * 2)], aB0: [2, new Float32Array(nI * 2)], aB1: [2, new Float32Array(nI * 2)], aG: [2, new Float32Array(nI * 2)], aG2: [2, new Float32Array(nI * 2)], aInfo: [4, new Float32Array(nI * 4)] };
    for (let i = 0; i < nI; i++) this._iattr.aCell[1][i] = Math.floor(i / RING);
    for (const [name, [sz, arr]] of Object.entries(this._iattr)) this._crackGeo.setAttribute(name, new THREE.InstancedBufferAttribute(arr, sz));
    this._crackGeo.instanceCount = 0;
    this._crackGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._crackMat = new THREE.ShaderMaterial({
      uniforms: this._crackU, vertexShader: crackVertex, fragmentShader: crackFragment, transparent: true, depthWrite: false, depthTest: true,
      blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor,
      blendEquationAlpha: THREE.MaxEquation, blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneFactor
    });
    this._cracks = new THREE.Mesh(this._crackGeo, this._crackMat);
    this._cracks.frustumCulled = false; this._cracks.renderOrder = 1;
    this._group = new THREE.Group(); this._group.add(this._shards); this._group.add(this._cracks); scene.add(this._group);

    // comfort: a tiny render of the scene, read back twice a second
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_W, LUM_H, { depthBuffer: true, stencilBuffer: false });

    // the first glass, for the aspect we will learn at the first frame
    this._camDist = 1 / Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    this._placeCamera(0, 1, 0);
    this._newGlass(16 / 9, Math.round(this._target * 0.55));
    this._bufSize = new THREE.Vector2();
  }

  // ---- the glass lifecycle -------------------------------------------------

  _rectFor(aspect) { this._hx = aspect * BLEED; this._hy = BLEED; }

  // a fresh glass for this aspect, with `count` seeds (R19: the impact is the stage centre)
  _newGlass(aspect, count) {
    this._rectFor(aspect);
    this._sites = poissonSites(this._rng, this._hx, this._hy, count);
    this._glass = buildGlass(this._sites, this._hx, this._hy);
    this._births = new Map();
    this._slots = this._glass.cells.map((c) => this._slotFor(c, c));
    this._writeGlass(this._glass, this._glass, null, -1e9);
    this._morph = 0;
    this._burstInScene = false;
  }

  _slotFor(a, b) {
    return { a, b, off: new THREE.Vector3(), vel: new THREE.Vector3(), q: new THREE.Quaternion(), q0: new THREE.Quaternion(), off0: new THREE.Vector3(), ang: new THREE.Vector3(), scale: 1, kind: 'both', spin: 0 };
  }

  // new seeds near a point, on the bar (the crack spreads live)
  _addCracks(count) {
    if (this._sites.length + count > Math.min(this._target + 20, MAX_CELLS - 2)) return;
    const rng = this._rng;
    // near the impact more often than not, otherwise anywhere in the frame
    let px, py;
    if (rng() < 0.55) { const r = 0.45 * Math.sqrt(rng()), a = rng() * Math.PI * 2; px = Math.cos(a) * r * this._hx / BLEED; py = Math.sin(a) * r; }
    else { px = (rng() * 2 - 1) * this._hx * 0.8; py = (rng() * 2 - 1) * this._hy * 0.8; }
    const R = 0.16 + 0.10 * rng();
    const added = [];
    for (let k = 0; k < count * 30 && added.length < count; k++) {
      const r = R * Math.sqrt(rng()), a = rng() * Math.PI * 2;
      const x = Math.max(-this._hx * 0.985, Math.min(this._hx * 0.985, px + Math.cos(a) * r)), y = Math.max(-this._hy * 0.985, Math.min(this._hy * 0.985, py + Math.sin(a) * r));
      let ok = true;
      for (const s of this._sites) if (Math.hypot(s.x - x, s.y - y) < 0.028) { ok = false; break; }
      if (ok) { this._sites.push({ x, y }); added.push({ x, y }); }
    }
    if (!added.length) return;
    this._glass = buildGlass(this._sites, this._hx, this._hy, { x: px, y: py });
    // slots follow their site (sites are only appended)
    for (let i = 0; i < this._glass.cells.length; i++) {
      const c = this._glass.cells[i];
      if (i < this._slots.length) { this._slots[i].a = c; this._slots[i].b = c; } else this._slots.push(this._slotFor(c, c));
    }
    this._writeGlass(this._glass, this._glass, { x: px, y: py }, this._time);
  }

  // the attributes of every slot: shards (rings A and B) and cracks (ring A's sides)
  _writeGlass(glassA, glassB, from, now) {
    const slots = this._slots, A = this._attr, I = this._iattr, VPC = this._VPC;
    const births = new Map();
    const lens = [];
    for (const s of slots) for (let k = 0; k < RING; k++) { const p = s.a.ring[k], q = s.a.ring[(k + 1) % RING]; const l = Math.hypot(q.x - p.x, q.y - p.y); if (l > 1e-6 && p.tag >= 0) lens.push(l); }
    lens.sort((a, b) => a - b);
    const medLen = lens.length ? lens[lens.length >> 1] : 0.1;
    for (let si = 0; si < slots.length; si++) {
      const s = slots[si], a = s.a, b = s.b;
      const base = si * VPC;
      for (let k = 0; k < RING; k++) {
        const pa = a.ring[k], pb = b.ring[k];
        const ax = pa.x - a.cx, ay = pa.y - a.cy, bx = pb.x - b.cx, by = pb.y - b.cy;
        // front and back ring vertices
        for (const [vi, z] of [[base + k, DEPTH / 2], [base + RING + k, -DEPTH / 2]]) {
          A.aPA[vi * 3] = ax; A.aPA[vi * 3 + 1] = ay; A.aPA[vi * 3 + 2] = z;
          A.aPB[vi * 3] = bx; A.aPB[vi * 3 + 1] = by; A.aPB[vi * 3 + 2] = z;
          const nz = z > 0 ? 1 : -1;
          A.aNA[vi * 3] = 0; A.aNA[vi * 3 + 1] = 0; A.aNA[vi * 3 + 2] = nz;
          A.aNB[vi * 3] = 0; A.aNB[vi * 3 + 1] = 0; A.aNB[vi * 3 + 2] = nz;
        }
        // the side k: 4 vertices, outward normal
        const qa = a.ring[(k + 1) % RING], qb = b.ring[(k + 1) % RING];
        const ax2 = qa.x - a.cx, ay2 = qa.y - a.cy, bx2 = qb.x - b.cx, by2 = qb.y - b.cy;
        const nax = ay2 - ay, nay = -(ax2 - ax), nal = Math.hypot(nax, nay) || 1;
        const nbx = by2 - by, nby = -(bx2 - bx), nbl = Math.hypot(nbx, nby) || 1;
        const sv = base + 2 * RING + k * 4;
        const corners = [[ax, ay, bx, by, -DEPTH / 2], [ax2, ay2, bx2, by2, -DEPTH / 2], [ax2, ay2, bx2, by2, DEPTH / 2], [ax, ay, bx, by, DEPTH / 2]];
        for (let c = 0; c < 4; c++) {
          const vi = sv + c, [x1, y1, x2, y2, z] = corners[c];
          A.aPA[vi * 3] = x1; A.aPA[vi * 3 + 1] = y1; A.aPA[vi * 3 + 2] = z;
          A.aPB[vi * 3] = x2; A.aPB[vi * 3 + 1] = y2; A.aPB[vi * 3 + 2] = z;
          A.aNA[vi * 3] = nax / nal; A.aNA[vi * 3 + 1] = nay / nal; A.aNA[vi * 3 + 2] = 0;
          A.aNB[vi * 3] = nbx / nbl; A.aNB[vi * 3 + 1] = nby / nbl; A.aNB[vi * 3 + 2] = 0;
        }
        // the crack instance for side k
        const ii = si * RING + k;
        I.aA0[1][ii * 2] = ax; I.aA0[1][ii * 2 + 1] = ay; I.aA1[1][ii * 2] = ax2; I.aA1[1][ii * 2 + 1] = ay2;
        I.aB0[1][ii * 2] = bx; I.aB0[1][ii * 2 + 1] = by; I.aB1[1][ii * 2] = bx2; I.aB1[1][ii * 2 + 1] = by2;
        I.aG[1][ii * 2] = pa.g; I.aG[1][ii * 2 + 1] = qa.g;
        I.aG2[1][ii * 2] = pa.g2 || 0; I.aG2[1][ii * 2 + 1] = qa.g2 || 0;
        const len = Math.hypot(ax2 - ax, ay2 - ay);
        const vis = pa.tag >= 0 && len > 1e-5 ? 1 : 0;
        I.aVis[1][ii] = vis;
        // radial: aligned with the direction to the impact, near it
        const mx = (pa.x + qa.x) / 2, my = (pa.y + qa.y) / 2, mr = Math.hypot(mx, my) || 1e-6;
        const ex = (ax2 - ax) / (len || 1), ey = (ay2 - ay) / (len || 1);
        const radial = smooth01((Math.abs(ex * mx / mr + ey * my / mr) - 0.72) / 0.22) * (1 - smooth01((mr - 0.45) / 0.5));
        // the birth: the pair of seeds identifies the crack across rebuilds
        const other = pa.tag, id = a.id;
        const key = other >= 0 ? (id < other ? id * 4096 + other : other * 4096 + id) : -1;
        let birth = -1e9, dir = 0;
        if (key >= 0) {
          const old = this._births.get(key);
          if (old) { birth = old.birth; dir = old.dir; }
          else if (from) {
            birth = now;
            // drawn from the end nearest the new seeds
            dir = Math.hypot(pa.x - from.x, pa.y - from.y) <= Math.hypot(qa.x - from.x, qa.y - from.y) ? 0 : 1;
          }
          births.set(key, { birth, dir });
        }
        I.aInfo[1][ii * 4] = radial; I.aInfo[1][ii * 4 + 1] = birth; I.aInfo[1][ii * 4 + 2] = dir; I.aInfo[1][ii * 4 + 3] = len < 0.6 * medLen ? 1 : 0;
      }
    }
    this._births = births;
    for (const name of Object.keys(A)) { const at = this._shardGeo.getAttribute(name); at.needsUpdate = true; at.updateRange = { offset: 0, count: -1 }; }
    this._shardGeo.getAttribute('position').needsUpdate = true;
    for (const name of Object.keys(I)) this._crackGeo.getAttribute(name).needsUpdate = true;
    this._shardGeo.setDrawRange(0, slots.length * this._IPC);
    this._crackGeo.instanceCount = slots.length * RING;
    this._crackU.uGmax.value = glassA.gmax;
    this._gmax = glassA.gmax;
  }

  // the burst: a new glass is built now, the shards fly, then return to it
  _burst() {
    if (this._state !== 'intact') return;
    this._state = 'flight'; this._stateT = 0; this._burstCount++; this._burstInScene = true;
    const rng = this._rng;
    for (const s of this._slots) {
      const c = s.a, r = Math.hypot(c.cx, c.cy);
      const jx = (rng() - 0.5) * 0.25, jy = (rng() - 0.5) * 0.25;
      const dx = c.cx + jx, dy = c.cy + jy, dl = Math.hypot(dx, dy) || 1;
      const sp = (0.35 + 0.55 * rng()) * (0.45 + 0.55 / (r + 0.35));
      s.vel.set(dx / dl * sp, dy / dl * sp, (rng() - 0.45) * 0.6);
      s.ang.set(rng() - 0.5, rng() - 0.5, rng() - 0.5).normalize().multiplyScalar(0.8 + 2.5 * rng());
      s.off.set(0, 0, 0); s.q.identity(); s.scale = 1; s.kind = 'both';
    }
    // the next glass, and the pairing by proximity
    const nextSites = poissonSites(rng, this._hx, this._hy, Math.round(this._target * 0.55));
    const next = buildGlass(nextSites, this._hx, this._hy);
    const pairs = [];
    for (let i = 0; i < this._slots.length; i++) for (let j = 0; j < next.cells.length; j++) {
      const a = this._slots[i].a, b = next.cells[j];
      pairs.push([Math.hypot(a.cx - b.cx, a.cy - b.cy), i, j]);
    }
    pairs.sort((p, q) => p[0] - q[0]);
    const usedI = new Uint8Array(this._slots.length), usedJ = new Uint8Array(next.cells.length);
    for (const [, i, j] of pairs) { if (usedI[i] || usedJ[j]) continue; usedI[i] = 1; usedJ[j] = 1; this._slots[i].b = next.cells[j]; }
    // shards without a cell shrink away; cells without a shard are born from a point
    for (let i = 0; i < this._slots.length; i++) if (!usedI[i]) {
      const s = this._slots[i], c = s.a, r = Math.hypot(c.cx, c.cy) || 1;
      const far = { x: c.cx / r * (this._hx + 1.5), y: c.cy / r * (this._hy + 1.5) };
      s.b = { id: -1, ring: c.ring.map(() => ({ x: far.x, y: far.y, g: 0, tag: -1 })), cx: far.x, cy: far.y, area: 0 };
      s.kind = 'dying';
    }
    for (let j = 0; j < next.cells.length; j++) if (!usedJ[j]) {
      const c = next.cells[j], r = Math.hypot(c.cx, c.cy) || 1;
      const s = this._slotFor(c, c);
      s.kind = 'born'; s.scale = 0;
      s.off.set(c.cx / r * 2.5, c.cy / r * 2.5, 0.6 * (rng() - 0.3));
      s.q.setFromAxisAngle(_v.set(rng() - 0.5, rng() - 0.5, rng() - 0.5).normalize(), rng() * 3);
      this._slots.push(s);
    }
    if (this._slots.length > MAX_CELLS) this._slots.length = MAX_CELLS;
    this._next = next;
    this._writeGlass(this._glass, next, null, -1e9);
  }

  _recompose() {
    this._state = 'recompose'; this._stateT = 0;
    for (const s of this._slots) { s.off0.copy(s.off); s.q0.copy(s.q); }
  }

  _finish() {
    // the new glass stands: slots are its cells, A := B
    const next = this._next;
    this._glass = next; this._sites = next.sites.slice();
    this._slots = next.cells.map((c) => this._slotFor(c, c));
    this._births = new Map();
    this._writeGlass(next, next, null, -1e9);
    this._morph = 0; this._next = null; this._state = 'intact'; this._stateT = 0; this._recede = 0;
    this._burstInScene = false;
    this._clock.restart(0);
    this._dropHoldUntil = this._time + 2 * 4 * this._beatPeriod;
    if (this._comfort.requestFlash(this._time)) this._flash = VDJ_DROP_FLASH;
  }

  // ---- per frame ---------------------------------------------------------

  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    const src = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : ACCENT_FALLBACK;
    const key = src.join('|');
    if (key === this._palKey && this._palIdxApplied === this._palIdx) return;
    this._palKey = key; this._palIdxApplied = this._palIdx;
    const cols = src.map((h) => vdjColor(h));
    const a = cols[this._palIdx % cols.length];
    // the second colour: the farthest hue, pushed apart if still too close (R4: 0.25)
    const hsl = { h: 0, s: 0, l: 0 }; a.getHSL(hsl); const ha = hsl.h;
    let b = null, bd = -1;
    for (const c of cols) { c.getHSL(hsl); const d = Math.min(Math.abs(hsl.h - ha), 1 - Math.abs(hsl.h - ha)); if (d > bd) { bd = d; b = c; } }
    b = b.clone();
    if (bd < 0.25) { b.copy(a); b.offsetHSL(0.33, 0, 0); }
    this._cols[0].copy(a); this._cols[1].copy(b);
    this._shardU.uTint.value.copy(a).lerp(b, 0.35);
  }

  _placeCamera(barPhase, zoom, roll) {
    const cam = this._camera; if (!cam) return;
    const d = this._camDist * (1 + 0.6 * this._recede);
    const t = this._time;
    const drift = this._recede * 0.12;
    cam.position.set(Math.sin(t * 0.7) * drift, Math.cos(t * 0.5) * drift * 0.6, d);
    cam.up.set(Math.sin(roll), Math.cos(roll), 0);
    cam.lookAt(0, 0, 0);
    cam.fov = FOV / zoom; cam.near = 0.05; cam.far = 50;
    cam.updateProjectionMatrix();
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== undefined && profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    this._applyPalette(profile);
    this._clock.update(dt);
    if (paused) return;                                           // the glass holds
    this._time += dt;
    const director = profile.director || {};
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    this._beatPeriod = 60 / THREE.MathUtils.clamp(bpm, 60, 200);
    const bands = director.hasReliableGrid !== false && director.bands ? director.bands : null;
    const low = bands ? clamp01(bands.low || 0) : 0.4, high = bands ? clamp01(bands.high || 0) : 0.3;
    this._low += (low - this._low) * Math.min(1, dt / 0.08);
    this._high += (high - this._high) * Math.min(1, dt / 0.05);

    // the beat clock (grid when there is one, a free 128 otherwise)
    const beatPhase = director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true;
      this._lastBeatPhase = beatPhase;
      this._beats += dt / this._beatPeriod;
    } else {
      this._free += dt;
      if (this._free >= this._beatPeriod) { this._free -= this._beatPeriod; beatEdge = true; }
      this._beats += dt / this._beatPeriod;
    }
    const bp = beatPhase != null ? beatPhase : this._free / this._beatPeriod;
    if (beatEdge) { this._beatCount++; this._lastBeatAt = this._time; }
    const barPhase = director.bar != null ? director.bar : profile.barPhase;
    let barEdge = false;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) barEdge = true; this._lastBarPhase = barPhase; }
    else if (beatEdge && this._beatCount % 4 === 1 && this._beatCount > 1) barEdge = true;
    const phrasePhase = director.phrase != null ? director.phrase : profile.phrasePhase;
    let phraseEdge = false;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) phraseEdge = true; this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % 32 === 1 && this._beatCount > 1) phraseEdge = true;
    if (barEdge) this._barCount++;
    if (phraseEdge) this._palIdx++;

    // the build: a drop announced within 8 bars
    const ev = director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const build = beatsToDrop != null && beatsToDrop < 32 ? clamp01((32 - beatsToDrop) / 28) : (director.mode === 'build' ? 0.4 : 0);
    this._build += (build - this._build) * Math.min(1, dt / 0.5);

    // the frame: a new aspect means a new glass (rare: a resize)
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    if (Math.abs(aspect - this._aspect) > 0.02) {
      this._aspect = aspect;
      if (this._state === 'intact') this._newGlass(aspect, Math.max(this._sites ? this._sites.length : 0, Math.round(this._target * 0.55)));
    }

    // the drop: the announced mark crossed this frame, or the scene's end, or the debug flag
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    let drop = false;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; drop = true; }
    }
    if (this._burstNow && this._time > 0.2) { this._burstNow = false; drop = true; }
    if (this._clock.wrapped && !this._burstInScene) drop = true;
    if (drop && this._state === 'intact' && this._time >= this._dropHoldUntil) this._burst();

    // the bar: new cracks (and twice a bar while building up)
    if (this._state === 'intact') {
      if (barEdge) this._addCracks(8 + Math.floor(this._rng() * 8));
      else if (this._build > 0.3 && beatEdge && this._beatCount % 4 === 3) this._addCracks(4 + Math.floor(this._rng() * 4));
    }

    // the flight and the return
    this._stateT += dt;
    const bar = 4 * this._beatPeriod;
    if (this._state === 'flight') {
      const drag = Math.exp(-dt * 1.0);
      for (const s of this._slots) {
        s.off.addScaledVector(s.vel, dt); s.vel.multiplyScalar(drag);
        const w = s.ang.length();
        if (w > 1e-6) { _q.setFromAxisAngle(_v.copy(s.ang).divideScalar(w), w * dt); s.q.premultiply(_q); }
      }
      this._recede = smooth01(this._stateT / (FLIGHT_BARS * bar));
      if (this._stateT >= FLIGHT_BARS * bar) this._recompose();
    } else if (this._state === 'recompose') {
      const e = smooth01(this._stateT / (RECOMPOSE_BARS * bar));
      this._morph = e;
      _qi.identity();
      for (const s of this._slots) {
        s.off.copy(s.off0).multiplyScalar(1 - e);
        s.q.copy(s.q0).slerp(_qi, e);
        s.scale = s.kind === 'dying' ? 1 - e : s.kind === 'born' ? e : 1;
      }
      this._recede = 1 - e;
      if (this._stateT >= RECOMPOSE_BARS * bar) this._finish();
    }

    // the cell texture
    const D = this._cellData, m = this._morph;
    for (let i = 0; i < this._slots.length; i++) {
      const s = this._slots[i], o = i * 8;
      D[o] = s.a.cx + (s.b.cx - s.a.cx) * m + s.off.x; D[o + 1] = s.a.cy + (s.b.cy - s.a.cy) * m + s.off.y; D[o + 2] = s.off.z; D[o + 3] = s.scale;
      D[o + 4] = s.q.x; D[o + 5] = s.q.y; D[o + 6] = s.q.z; D[o + 7] = s.q.w;
    }
    this._cellTex.needsUpdate = true;

    // the look
    const kick = vdjCameraKick(barPhase != null ? barPhase : (this._beats % 4) / 4, this._barCount, 4);
    this._placeCamera(barPhase, kick.zoom, kick.roll);
    this._post.update(vdjBeatChroma(bp, this._beatPeriod));
    const flying = this._state !== 'intact';
    const L = this._shardU.uLight.value;
    // the projector: a direction 25 deg off the view axis, turning slowly; fast and strong in flight (flashes of reflections)
    const spinRate = flying && this._layout.spin ? 1.3 : (this._layout.spin ? 0.12 : 0);
    this._lightA = (this._lightA || 0.8) + dt * spinRate;
    L.set(Math.cos(this._lightA) * 0.45, Math.sin(this._lightA) * 0.45, 1.0);
    this._shardU.uSpecK.value = this._layout.specK * (flying ? 9.0 : 1);
    this._shardU.uSpecPow.value = this._layout.specPow * (flying ? 1.5 : 1);
    const cu = this._crackU, sh = this._shardU;
    cu.uTime.value = this._time; sh.uTime.value = this._time;
    cu.uMorph.value = m; sh.uMorph.value = m;
    const shake = flying ? 0 : this._build * this._build * 3.0;
    cu.uShake.value = shake; sh.uShake.value = shake;
    // the pulse: from the impact to the edges in a half beat (a third while building)
    const travel = this._beatPeriod * (0.5 - 0.2 * this._build);
    const since = this._time - this._lastBeatAt;
    cu.uPulseS.value = since / travel * this._gmax;
    cu.uPulseW.value = 0.10 * this._gmax;
    cu.uPulseA.value = 0.95 * (1 - 0.5 * clamp01((since - travel) / travel));
    cu.uBase.value = 0.30 + 0.10 * this._build;
    cu.uHigh.value = this._high;
    cu.uBreath.value = this._low;
    cu.uDraw.value = 0.6 / this._beatPeriod;                   // the front: 0.6 pane units per beat
    cu.uApart.value = 1;
    cu.uCore.value = 0.9 + 0.3 * this._build; cu.uSheath.value = 1.5;
    this._flashU.value = this._flash; this._flash = 0;
    // comfort
    this._lumAge += dt;
    if (this._lumAge > 0.5) { this._lumAge = 0; this._lumPending = true; }
    if (this._lumFresh) { this._lumFresh = false; this._gain.value = this._comfort.measure(this._lumMeasured, this._lumSince || 0.5); this._lumSince = 0; }
    this._lumSince = (this._lumSince || 0) + dt;
  }

  _beforeRender(renderer, scene, camera) {
    if (this._measuring) return;
    const size = renderer.getDrawingBufferSize(this._bufSize);
    const t = renderer.getRenderTarget();
    if (t) this._shardU.uScreen.value.set(t.width, t.height); else this._shardU.uScreen.value.copy(size);
    if (!this._blendChecked) {
      this._blendChecked = true;
      const ok = renderer.capabilities.isWebGL2 || renderer.extensions.has('EXT_blend_minmax');
      if (!ok) { this._crackMat.blending = THREE.AdditiveBlending; this._crackMat.needsUpdate = true; }
    }
    if (this._lumPending) {
      this._lumPending = false; this._measuring = true;
      const prevScreen = this._shardU.uScreen.value.clone();
      this._shardU.uScreen.value.set(LUM_W, LUM_H);
      renderer.setRenderTarget(this._lumTarget);
      renderer.clear();
      renderer.render(scene, camera);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_W, LUM_H, this._lumPixels);
      renderer.setRenderTarget(t);
      this._shardU.uScreen.value.copy(prevScreen);
      this._measuring = false;
      let sum = 0; const px = this._lumPixels;
      for (let i = 0; i < px.length; i += 4) sum += (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) / 255;
      this._lumMeasured = sum / (LUM_W * LUM_H);
      this._lumFresh = true;
    }
  }

  get probe() {
    return {
      tier: this._tier, state: this._state, stateT: +this._stateT.toFixed(2), cells: this._slots.length, sites: this._sites ? this._sites.length : 0,
      gmax: +(this._gmax || 0).toFixed(3), build: +this._build.toFixed(2), bursts: this._burstCount, scene: +this._clock.t.toFixed(2),
      recede: +this._recede.toFixed(2), morph: +this._morph.toFixed(2), gain: +this._gain.value.toFixed(2), lum: +this._lumMeasured.toFixed(3),
      beatPeriod: this._beatPeriod, pulseS: +this._crackU.uPulseS.value.toFixed(3),
      offsets: this._slots.slice(0, 4).map((s) => +s.off.length().toFixed(3)),
      glass: this._glass ? this._glass.cells.map((c) => ({ ring: c.ring.map((p) => [p.x, p.y]), area: c.area })) : null,
      rect: [this._hx, this._hy],
      camera: this._camera ? { fov: this._camera.fov, aspect: this._camera.aspect, z: +this._camera.position.z.toFixed(3) } : null
    };
  }

  dispose() {
    if (this._shards) this._shards.onBeforeRender = null;
    if (this._group && this._scene) this._scene.remove(this._group);
    if (this._shardGeo) this._shardGeo.dispose();
    if (this._crackGeo) this._crackGeo.dispose();
    if (this._shardMat) this._shardMat.dispose();
    if (this._crackMat) this._crackMat.dispose();
    if (this._cellTex) this._cellTex.dispose();
    if (this._lumTarget) this._lumTarget.dispose();
    if (this._scene) this._scene.background = this._prevBackground || null;
    if (this._camera) { this._camera.up.set(0, 1, 0); this._camera.fov = 68; /* scene-manager's BASE_FOV */ this._camera.near = 0.1; this._camera.far = 1000; this._camera.updateProjectionMatrix(); }
    this._scene = null; this._camera = null; this._slots = [];
  }
}
